/**
 * Ref-less snapshot diff (fast-drive mode).
 *
 * The ref-based `snapshot-diff.ts` keys every element on `el.ref`, so it can't
 * compare ref-less snapshots. This is a lean structural diff for fast-drive:
 * it keys interactive elements on `role + name` (a `getByRole` target) and
 * reports what appeared / disappeared after an action, plus navigation.
 *
 * Pure: operates on two captured YAML strings — no DOM/browser access.
 */
import { parseSnapshot, type SnapshotElement } from './snapshot-analyzer.js';
import { INTERACTIVE_ROLES } from './snapshot-formatter-shared.js';

export interface ReflessDiffOptions {
  beforeUrl?: string;
  afterUrl?: string;
  /** Max element names listed per added/removed bucket before eliding. */
  maxList?: number;
}

const DEFAULT_MAX_LIST = 8;

function flatten(roots: SnapshotElement[]): SnapshotElement[] {
  const out: SnapshotElement[] = [];
  const stack = [...roots];
  while (stack.length > 0) {
    const el = stack.pop()!;
    out.push(el);
    stack.push(...el.children);
  }
  return out;
}

/** Count of each interactive `role "name"` target in a ref-less snapshot. */
function interactiveCounts(yaml: string): Map<string, number> {
  const counts = new Map<string, number>();
  for (const el of flatten(parseSnapshot(yaml))) {
    if (!INTERACTIVE_ROLES.has(el.role)) continue;
    const name = (el.text ?? '').replace(/\s+/g, ' ').trim();
    if (!name) continue;
    const key = `${el.role} "${name}"`;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return counts;
}

function bucket(map: Map<string, number>, other: Map<string, number>): string[] {
  const out: string[] = [];
  for (const [key, n] of map) {
    const delta = n - (other.get(key) ?? 0);
    if (delta > 0) out.push(delta > 1 ? `${key} ×${delta}` : key);
  }
  return out;
}

function list(items: string[], max: number): string {
  if (items.length <= max) return items.join(', ');
  return `${items.slice(0, max).join(', ')}, … (+${items.length - max} more)`;
}

/**
 * Compare two ref-less snapshot YAMLs and return a compact, agent-facing delta.
 * Byte-identical input short-circuits to "no DOM change".
 */
export function diffReflessSnapshots(
  before: string,
  after: string,
  opts?: ReflessDiffOptions
): string {
  const navigated = opts?.afterUrl !== undefined && opts.beforeUrl !== opts.afterUrl;
  const navPart = navigated ? `navigated → ${opts!.afterUrl}` : null;

  if (before === after) {
    return navPart ?? 'no DOM change.';
  }

  const max = opts?.maxList ?? DEFAULT_MAX_LIST;
  const beforeCounts = interactiveCounts(before);
  const afterCounts = interactiveCounts(after);
  const added = bucket(afterCounts, beforeCounts);
  const removed = bucket(beforeCounts, afterCounts);

  const parts: string[] = [];
  if (navPart) parts.push(navPart);
  if (added.length > 0) parts.push(`+${added.length} interactive: ${list(added, max)}`);
  if (removed.length > 0) parts.push(`-${removed.length} interactive: ${list(removed, max)}`);
  if (parts.length === 0) {
    // YAML differs but the interactive set is identical — text/state-only change.
    parts.push('DOM changed (text/state only) — call browser_snapshot --refless for detail.');
  }
  return parts.join(' | ');
}
